import * as React from 'react'

import { BusinessTripModel } from '@/app/models/businessTripModel'
import GroupedBusinessTripList from '../components/GroupedBusinessTripList'
import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import BusinessTripActionBar from '@/modules/drivers/shared/actionBars/BusinessTripActionBar'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'

interface GroupProps {
  trips: BusinessTripModel[]
  companyId: string
  allDrivers?: { fullName: string; id: number; isActive: boolean }[]
  match?: RouteComponentProps['match']
  history?: RouteComponentProps['history']
  dateRangeFilter?: DateRangeFilter
  driverIds: string[]
  onDateRangeFilterChange?: (filter: DateRangeFilter) => void
  handleRefresh: () => void
  showAuditDates?: boolean
}

const getTripId = (trip: BusinessTripModel): number =>
  typeof trip.id === 'string' ? parseInt(trip.id, 10) : (trip.id as number)

const Group: React.FC<GroupProps> = ({
  trips,
  companyId,
  allDrivers,
  handleRefresh,
  dateRangeFilter,
  onDateRangeFilterChange,
  showAuditDates: showAuditDatesProp = true,
}) => {
  const [showAuditDates, setShowAuditDates] = React.useState(showAuditDatesProp)
  const { t } = useLocalize()
  const { deleteBusinessTrips } = useBusinessTripService()
  const confirm = useConfirm()

  const runDelete = React.useCallback(
    async (ids: number[]) => {
      const validIds = ids.filter((id) => Number.isFinite(id))
      if (validIds.length === 0) return
      try {
        await deleteBusinessTrips({ companyId, ids: validIds })
        toast.success({
          text: `Usunięto ${validIds.length} ${
            validIds.length === 1 ? 'delegację' : 'delegacji'
          }.`,
        })
        handleRefresh()
      } catch (error) {
        console.error('Error deleting business trips:', error)
        toast.error({
          text: t('error.delete') || 'Nie udało się usunąć delegacji.',
        })
      }
    },
    [deleteBusinessTrips, companyId, handleRefresh, t],
  )

  const handleDeleteAll = React.useCallback(async () => {
    const ids = trips.map(getTripId).filter((id) => Number.isFinite(id))
    if (ids.length === 0) return

    const driverCount = new Set(
      trips
        .map((trip) => String(trip.driver?.id ?? trip.driver ?? ''))
        .filter(Boolean),
    ).size

    const ok = await confirm({
      title: t('operation.delete') || 'Usuń delegacje',
      message: `Usuwasz ${ids.length} delegacji dla ${driverCount} ${
        driverCount === 1 ? 'kierowcy' : 'kierowców'
      }. Aby zmienić zakres, usuń kierowców z obszaru roboczego. Tej operacji nie można cofnąć.`,
      confirmText: t('operation.delete') || 'Usuń',
      cancelText: t('operation.cancel') || 'Anuluj',
    })
    if (!ok) return
    await runDelete(ids)
  }, [trips, confirm, runDelete, t])

  const handleDeleteTrip = React.useCallback(
    async (tripId: number) => {
      if (!Number.isFinite(tripId)) return
      const ok = await confirm({
        title: t('operation.delete') || 'Usuń delegację',
        message:
          'Czy na pewno usunąć tę delegację? Tej operacji nie można cofnąć.',
        confirmText: t('operation.delete') || 'Usuń',
        cancelText: t('operation.cancel') || 'Anuluj',
      })
      if (!ok) return
      await runDelete([tripId])
    },
    [confirm, runDelete, t],
  )

  return (
    <div className="tw-z-10 tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
      <BusinessTripActionBar
        companyId={companyId}
        onRefresh={handleRefresh}
        selectedTrips={trips}
        dateRangeFilter={dateRangeFilter}
        onDateRangeFilterChange={onDateRangeFilterChange}
        setShowAuditDates={setShowAuditDates}
        showAuditDates={showAuditDates}
        tripCount={trips.length}
        onDeleteAll={handleDeleteAll}
      />
      <GroupedBusinessTripList
        trips={trips}
        companyId={companyId}
        allDrivers={allDrivers}
        onRefresh={handleRefresh}
        showAuditDates={showAuditDates}
        onDeleteTrip={handleDeleteTrip}
      />
    </div>
  )
}

export default Group
